import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plane, AlertTriangle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useConfig } from '@/contexts/SystemConfigContext';
import { useSelfManifest } from '@/hooks/useFunJumper';
import { toast } from 'sonner';

const DISCIPLINES = [
  'freefly', 'tracking', 'angle', 'wingsuit',
  'large-formation', 'rw', 'canopy-piloting', 'hop-and-pop', 'other',
];

const SelfManifestDialog = ({ open, onOpenChange, load, profile }) => {
  const { t } = useTranslation();
  const { formatTime, formatWeight } = useConfig();
  const selfManifest = useSelfManifest();

  const [altitude, setAltitude] = useState(13500);
  const [discipline, setDiscipline] = useState('');
  const [renting, setRenting] = useState(profile?.rentingEquipment ?? false);

  const isFull = load?.slotsAvailable === 0;
  const reserveBlocked = !renting && profile?.hasValidReserve === false;

  const handleSubmit = () => {
    const payload = {
      loadId: load.loadId,
      jumpType: 'fun-jump',
    };
    if (altitude) payload.jumpAltitude = Number(altitude);
    if (discipline) payload.discipline = discipline;
    payload.rentingEquipment = renting;

    selfManifest.mutate(payload, {
      onSuccess: (result) => {
        if (result.outcome === 'MANIFESTED') {
          toast.success(t('funJumper.manifested'));
        } else if (result.outcome === 'WAITLISTED') {
          toast.info(t('funJumper.waitlisted', { position: result.data?.position }));
        }
        onOpenChange(false);
        setAltitude(13500);
        setDiscipline('');
        setRenting(profile?.rentingEquipment ?? false);
      },
      onError: (error) => {
        const code = error.response?.data?.code;
        const reasons = error.response?.data?.reasons;
        const message = error.response?.data?.message;
        // Sport-waiver just-in-time dispatch — backend just sent the
        // bundle to the customer's email. Show an info toast (not a hard
        // error) so the customer knows to check their inbox.
        if (code === 'WAIVER_DISPATCHED' || code === 'WAIVER_PENDING_SIGN') {
          toast.info(message || t('funJumper.errors.WAIVER_DISPATCHED', 'Sport waiver sent — please sign and try again.'), {
            duration: 8000,
          });
          // Close the dialog so the customer goes to their email; they
          // can come back and retry once signed.
          onOpenChange(false);
        } else if (code === 'WAIVER_BUNDLE_NOT_CONFIGURED') {
          // Bundle isn't set up at all. Distinct toast so it's clear
          // this is a config issue (DZ admin needs to act) rather than
          // a customer-fixable problem.
          toast.error(
            message || 'No sport-waiver bundle is configured for this jump type. Please contact the front desk.',
            { duration: 8000 },
          );
        } else if (code === 'WAIVER_DISPATCH_FAILED') {
          // Bundle is set up but emails/templates errored — also a
          // server-side problem, not customer-fixable.
          toast.error(
            message || "We couldn't send your sport waiver right now. Please contact the front desk.",
            { duration: 8000 },
          );
        } else if (code === 'INELIGIBLE' && reasons?.length > 0) {
          const reasonText = reasons.map(r => t(`funJumper.eligibility.reasons.${r.code}`, r.message)).join('\n');
          toast.error(reasonText, { duration: 6000 });
        } else if (code) {
          toast.error(t(`funJumper.errors.${code}`, message));
        } else {
          toast.error(message || t('funJumper.errors.generic'));
        }
      },
    });
  };

  if (!load) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('funJumper.dialog.title')}</DialogTitle>
          <DialogDescription>{t('funJumper.dialog.subtitle')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/50">
            <div className="flex items-center gap-2">
              <Plane className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-sm">{load.aircraft?.name}</span>
            </div>
            <div className="text-right text-sm">
              <div className="font-medium">{formatTime(load.scheduledAt)}</div>
              <div className="text-xs text-muted-foreground">
                {t('funJumper.loadCard.slots', {
                  available: load.slotsAvailable,
                  total: load.slotsTotal,
                })}
              </div>
            </div>
          </div>

          {profile && (
            <div className="space-y-3">
              {profile.weight && (
                <div className="text-sm text-muted-foreground">
                  {t('funJumper.dialog.weight')}: {formatWeight(profile.weight)}
                </div>
              )}
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <p className="text-sm font-medium">{t('funJumper.dialog.rentingLabel')}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {renting ? t('funJumper.dialog.rentingHint') : t('funJumper.dialog.ownGearHint')}
                  </p>
                </div>
                <Switch checked={renting} onCheckedChange={setRenting} />
              </div>
              {!renting && profile.hasValidReserve === false && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-3">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    {t('funJumper.dialog.reserveWarning')}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label>{t('funJumper.dialog.jumpType')}</Label>
            <Input value={t('funJumper.dialog.funJump')} disabled />
          </div>

          <div className="space-y-2">
            <Label>{t('funJumper.dialog.altitude')}</Label>
            <Input
              type="number"
              value={altitude}
              onChange={(e) => setAltitude(e.target.value)}
              min={3000}
              max={18000}
              step={500}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('funJumper.dialog.discipline')}</Label>
            <Select value={discipline} onValueChange={setDiscipline}>
              <SelectTrigger>
                <SelectValue placeholder={t('funJumper.dialog.disciplinePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {DISCIPLINES.map((d) => (
                  <SelectItem key={d} value={d}>
                    {t(`funJumper.dialog.disciplines.${d}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selfManifest.isPending || reserveBlocked}
            className={isFull ? 'bg-amber-600 hover:bg-amber-700' : ''}
          >
            {selfManifest.isPending
              ? t('funJumper.dialog.submitting')
              : isFull
                ? t('funJumper.dialog.joinWaitlist')
                : t('funJumper.dialog.manifestButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SelfManifestDialog;
